import type { Trip, TripWritable } from '@bambi/api-client/schemas';
import { Button } from '@bambi/component-library/src/components/Button/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { DateTime } from 'luxon';
import type { PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { TripImportedBadge } from '../trip/management/TripImportedBadge';
import { addATrip } from './addATrip.slice';
import { AddATripErrorMessageForm } from './AddATripErrorMessageForm';
import { assignDefaultFormValues } from './assignDefaultFormValues';
import { DuplicateTripsByLeg } from './duplicate-trip-check/DuplicateTripsByLeg';
import { hasDuplicateCriteriaFieldsDirty } from './duplicate-trip-check/hasDuplicateCriteriaFieldsDirty';
import type { LegMatches } from './duplicate-trip-check/useDuplicateTripCheck';
import { useDuplicateTripCheck } from './duplicate-trip-check/useDuplicateTripCheck';
import type { AddTripFormValues } from './FormValues';
import { useCanCreateTripInThePast } from './TripDetails/TripDateContextField/useCanCreateTripInThePast';
import { TripPriceEstimateResult } from './TripPriceEstimateResult/TripPriceEstimateResult';
import { useAddATripOnSubmit } from './useAddATripOnSubmit';
import { useUpdateATripOnSubmit } from './useAddATripOnSubmit/useUpdateATripOnSubmit';
import { validateFormInputs } from './useAddATripOnSubmit/validateFormInputs';
import { useIsEditingTrip } from './useIsEditingTrip';
import { useFeature } from 'src/app/FeatureToggle/useFeature';
import { useAppSelector } from 'src/app/store/hooks';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { FormSubmitButton } from 'src/common/FormSubmitButton';
import { ConfirmModal } from 'src/common/modals/ConfirmModal';
import { useModal } from 'src/common/modals/useModal';
import { useShouldShowRideOrderingPortalFeatures } from 'src/features/rideOrderingPortal/useShouldShowRideOrderingPortalFeatures';
import { TripStatusBadge } from 'src/features/trip/management/TripStatusBadge';

export function AddATripFlowForm({ children }: PropsWithChildren) {
  const shouldShowRideOrderingPortalFeatures =
    useShouldShowRideOrderingPortalFeatures();
  const isEditingTrip = useIsEditingTrip();

  const selectedTrip = useAppSelector((state) => state.addATrip.selectedTrip);
  const isOpen = useAppSelector((state) => state.addATrip.isOpen);
  const isSubmitting = useAppSelector((state) => state.addATrip.isSubmitting);
  const passenger = useAppSelector((state) => state.addATrip.selectedPassenger);
  const hasPassengerChangedFromSavedTrip =
    isEditingTrip && passenger?.id !== selectedTrip?.passenger.id;
  const errors = useAppSelector((state) => state.addATrip).errors;
  const { open: isConfirmModalOpen, setOpen: setConfirmModalOpen } = useModal();
  const { open: isDuplicateModalOpen, setOpen: setDuplicateModalOpen } =
    useModal();
  const [duplicateMatches, setDuplicateMatches] = useState<LegMatches[] | null>(
    null
  );
  const { checkDuplicates, isChecking } = useDuplicateTripCheck();
  const canCreateTripInThePast = useCanCreateTripInThePast();
  const selectedPayer = useAppSelector((state) => state.addATrip.selectedPayer);
  const { isEnabled: isTripRequiredFieldsEnabled } = useFeature(
    'trip required fields'
  );
  const [checkFailed, setCheckFailed] = useState(false);

  const methods = useForm({
    defaultValues: assignDefaultFormValues(selectedTrip as Trip & TripWritable),
    mode: 'all',
  });
  const dispatch = useDispatch();

  const onAddATripSubmit = useAddATripOnSubmit({
    getValues: methods.getValues,
  });

  const onUpdateATripSubmit = useUpdateATripOnSubmit({
    getValues: methods.getValues,
    getFieldState: methods.getFieldState,
  });

  useEffect(() => {
    // TODO: Need to refine canceling behavior or at least have save a draft before always resetting on close
    // Cancel should probably definitely reset
    // Clicking any element outside the dialog is a little trickier (ex: Clicking on a toast sometimes causes it to close, which is a bug)
    // By only resetting if the trip is successful this is deferred for a bit
    if (!isOpen) {
      dispatch(addATrip.actions.selectNoPassenger());
      dispatch(addATrip.actions.selectPayer(null));
      if (methods.formState.isSubmitSuccessful) methods.reset();
      setCheckFailed(false);
    }
  }, [isOpen, methods, dispatch]);

  useEffect(() => {
    if (errors.length > 0) {
      const submitErrors = document.querySelector('#add-a-trip-form-errors');
      if (submitErrors) {
        submitErrors.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [errors]);

  const onGoToError = (id: string) => {
    if (id === 'passenger') {
      const temp = { ...passenger };
      dispatch(addATrip.actions.selectNoPassenger());
      dispatch(addATrip.actions.selectPassenger(temp));
      dispatch(addATrip.actions.setIsEditingPassenger(true));
      return;
    }

    const error = document.querySelector(`#${id}`);
    if (error) {
      error.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const [openErrorId, setOpenErrorId] = useState<string | null>(null);
  const tripLegsPriceEstimationState = useAppSelector(
    (state) => state.addATrip.tripLegsPriceEstimationState
  );

  const isRevenueEstimateStale = tripLegsPriceEstimationState.some(
    (legState) => legState.isStale
  );

  const saveButtonLabel = isRevenueEstimateStale
    ? 'Save with stale price quotes'
    : 'Save';

  const proceedToSave = (values: AddTripFormValues) => {
    if (isEditingTrip) {
      return onUpdateATripSubmit(values);
    }
    if (
      canCreateTripInThePast &&
      DateTime.fromISO(values.trips[0].timing.date).startOf('day') <
        DateTime.now().startOf('day')
    ) {
      setConfirmModalOpen(true);
      return;
    }
    onAddATripSubmit(values);
  };

  const runDuplicateCheckAndDispatch = async (values: AddTripFormValues) => {
    const matches = await checkDuplicates(values);
    if (matches === null) {
      setCheckFailed(true);
      return;
    }
    if (matches.some((m) => m.matches.length > 0)) {
      setDuplicateMatches(matches);
      setDuplicateModalOpen(true);
      return;
    }
    proceedToSave(values);
  };

  const runPreSavePipeline = async (
    values: AddTripFormValues,
    { skipDuplicateCheck = false } = {}
  ) => {
    setCheckFailed(false);
    const validationErrors = validateFormInputs(
      values,
      selectedPayer,
      isTripRequiredFieldsEnabled
    );
    if (validationErrors.length > 0) {
      dispatch(addATrip.actions.onError(validationErrors));
      return;
    }
    if (skipDuplicateCheck) {
      proceedToSave(values);
      return;
    }
    // On edit, only run the duplicate check if the user changed a field
    // that contributes to the duplicate-match criteria (passenger,
    // pickup/dropoff place, date, pickup_time, is_will_call). Editing
    // unrelated fields like price or notes shouldn't surface duplicate
    // warnings. The check itself excludes the trip being edited via
    // excludeTripId, so self-matches never show regardless of mode.
    if (
      isEditingTrip &&
      !hasPassengerChangedFromSavedTrip &&
      !hasDuplicateCriteriaFieldsDirty(methods.formState.dirtyFields)
    ) {
      proceedToSave(values);
      return;
    }
    await runDuplicateCheckAndDispatch(values);
  };

  // Wraps handleSubmit to also clear checkFailed when RHF field validation
  // fails (second arg = onInvalid callback), so the inline error doesn't linger
  // underneath freshly-shown field errors.
  const handleSubmitWithReset = (
    handler: (values: AddTripFormValues) => void | Promise<void>
  ) =>
    methods.handleSubmit(handler, () => {
      setCheckFailed(false);
    });

  return (
    <FormProvider {...methods}>
      <form
        className="h-full"
        onSubmit={handleSubmitWithReset((values) => runPreSavePipeline(values))}
        data-testid="trip-form"
        data-tripid={selectedTrip?.id}
      >
        <div className="flex h-full w-[100vw] flex-col justify-between divide-y divide-gray-200">
          <div className="flex flex-grow flex-col gap-y-4 overflow-y-scroll px-4 pb-4 pt-2 sm:px-6">
            <h1 className="flex items-center gap-2 text-xl font-bold">
              <div className="flex w-full flex-row justify-between">
                <div className="flex items-center gap-2">
                  <span>{isEditingTrip ? 'Edit' : 'Add'} Trip</span>
                  {isEditingTrip && (
                    <span data-test-id="trip-form-trip-status-badge">
                      <TripStatusBadge status={selectedTrip?.status} />
                    </span>
                  )}
                  {isEditingTrip && selectedTrip && (
                    <span data-test-id="trip-form-trip-imported-badge">
                      <TripImportedBadge trip={selectedTrip} />
                    </span>
                  )}
                </div>

                <span>
                  {isEditingTrip ? (
                    <span className="text-lg">
                      {selectedTrip?.confirmation_code}
                    </span>
                  ) : null}
                </span>
              </div>
            </h1>
            {errors.map((error) => {
              // Tried src/common/util/serverErrorFormatter.ts but wanted to generalize and make a more declarative abstraction
              // TODO: ids aren't being assigned properly to HeadlessUI components, so going to the error won't currently work all the time
              // However, it should be rare that any of these aren't caught by client side validation.
              // I'd like to do a few things:
              // 1. Fix the ids properly
              // 2. Clean up attr formatting to make this prettier or use the field label
              // 3. Standardize the type. { attr: string, code: string, detail: string } from the server works pretty well but I could see an optional type
              // although code seems to be more of a type/category ('required', 'null', etc)
              // 4. Smooth out the scrolling
              // 5. Use react hook form to highlight fields and something like https://www.carlrippon.com/react-hook-form-server-validation/
              // Don't want to blow up this pr though
              const errorId = `${error.attr}-${error.detail}`;
              return (
                <div key={errorId}>
                  <FormErrorMessage>
                    <div id="add-a-trip-form-errors">
                      {error.attr}: {error.genericMessage}
                      {error.code !== 'general' && (
                        <span
                          className="cursor-pointer underline"
                          onClick={() => onGoToError(error.attr.toLowerCase())}
                        >
                          Go to error
                        </span>
                      )}
                      <Button
                        onClick={() => setOpenErrorId(errorId)}
                        className="ml-2"
                        variant="secondary"
                      >
                        Show more
                      </Button>
                    </div>
                  </FormErrorMessage>
                  <AddATripErrorMessageForm
                    open={openErrorId === errorId}
                    setOpen={(isOpen) =>
                      setOpenErrorId(isOpen ? errorId : null)
                    }
                    onClose={() => {
                      setOpenErrorId(null);
                    }}
                    errorDetail={
                      error.detail
                        ? error.detail.toString()
                        : 'Unexpected error...'
                    }
                  />
                </div>
              );
            })}
            {children}
          </div>
          <div className="flex flex-shrink-0 justify-between gap-x-1 bg-white px-4 py-4 sm:px-6">
            {!shouldShowRideOrderingPortalFeatures && (
              <TripPriceEstimateResult />
            )}
            <ConfirmModal
              open={isConfirmModalOpen}
              setOpen={setConfirmModalOpen}
              title="Trip Date is in the past"
              description="You are creating a trip for a past date! Are you sure you want to do that?"
              onCancel={() => {
                setConfirmModalOpen(false);
              }}
              onConfirm={() => {
                setConfirmModalOpen(false);
                const values = methods.getValues();
                onAddATripSubmit(values);
              }}
              confirmText="Yes, I am sure"
            />
            <ConfirmModal
              open={isDuplicateModalOpen}
              setOpen={setDuplicateModalOpen}
              mode="neutral"
              title="Possible duplicate trips found"
              description="Other trips for this passenger match what you're saving. Continue anyway?"
              contentClassnames="sm:max-w-[40rem]"
              onCancel={() => setDuplicateModalOpen(false)}
              onConfirm={handleSubmitWithReset((values) => {
                setDuplicateModalOpen(false);
                proceedToSave(values);
              })}
              cancelText="No, go back"
              confirmText="Yes, save trip"
            >
              {duplicateMatches && (
                <DuplicateTripsByLeg
                  legSpecs={methods.getValues().trips.map((t, i) => ({
                    timing: t.timing,
                    label: `Leg ${i + 1}`,
                  }))}
                  legMatches={duplicateMatches}
                />
              )}
            </ConfirmModal>
            <div className="flex w-full items-center justify-end gap-2">
              {(isSubmitting || isChecking) && <LoadingIndicator />}
              {checkFailed && (
                <div
                  role="alert"
                  data-testid="duplicate-check-failed"
                  className="flex w-full items-center justify-end gap-2 text-sm text-warning-700"
                >
                  <span>Couldn't check for duplicates.</span>
                  <Button
                    variant="secondary"
                    onClick={handleSubmitWithReset((values) =>
                      runPreSavePipeline(values)
                    )}
                  >
                    Retry
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleSubmitWithReset((values) =>
                      runPreSavePipeline(values, { skipDuplicateCheck: true })
                    )}
                  >
                    Save anyway
                  </Button>
                </div>
              )}
              <Button
                variant="ghost"
                onClick={() => dispatch(addATrip.actions.cancel())}
              >
                Cancel
              </Button>
              <FormSubmitButton
                isSubmitting={isSubmitting || isChecking}
                buttonName={saveButtonLabel}
              />
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
